'use client';

import type {ReactNode} from 'react';

import classNames from 'classnames';

import {TrackImpression} from '@udemy-v2/event-tracking';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Image} from '@udemy-v2/react-core-components';
import {RatingTag, Tag} from '@udemy-v2/react-product-card-components';

import type {CommunityListing} from '@/features/communities/lib/types';

import styles from './community-card.module.scss';

/**
 * `data-testid` of the wrapper `div` that `TrackImpression` renders. Exported because
 * both the stylesheet (which has no className hook to target) and the tests need it.
 */
export const CARD_IMPRESSION_TEST_ID = 'community-card-impression';

/**
 * `trackFunc` is required, and the wrapper renders unconditionally so the card's DOM
 * and layout are the same whether or not the caller tracks anything.
 */
function noop() {}

export interface CommunityCardProps {
    /** The community and its instructor. Both are guaranteed present by the mapper. */
    listing: CommunityListing;
    /**
     * Hides the card below the mobile breakpoint, leaving it visible on desktop.
     *
     * The two breakpoints reveal different numbers of cards (Figma draws 15 on desktop
     * and 6 on mobile), and the section resolves that in CSS rather than with a
     * `matchMedia` hook: the page is statically rendered, so a JS breakpoint check
     * would paint the desktop count first and narrow it after hydration.
     */
    isHiddenOnMobile?: boolean;
    /**
     * Called when the card has been visible to the learner, on the library's default
     * terms (99% visible for a cumulative 500ms, once per mount). Deliberately not
     * called on mount.
     */
    onPresented?: () => void;
    /** Called when the learner clicks the card, before the browser leaves the page. */
    onSelected?: () => void;
}

/**
 * One instructor community in the discover grid — Figma node 5260:156760 (desktop) and
 * 5270:55897 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A pale rounded surface: a circular instructor photo beside (mobile: above) the
 * instructor's name, their public marketplace stats, and the community's name and
 * categories. The whole surface is one link to the instructor's profile.
 *
 * Presentational and props-only, and renders an `<li>`, so it must sit inside the
 * section's grid list.
 *
 * The contract exposes no community URL, description or member count, so what is drawn
 * here is everything a card can show (see `docs/community-showcase-data-plan.md` §3).
 * Each stat is individually nullable, and null means *no data* rather than zero: a
 * missing `courseCount` hides its tag, while a real `0` renders as "0 Courses".
 */
export function CommunityCard({
    listing,
    isHiddenOnMobile = false,
    onPresented,
    onSelected,
}: CommunityCardProps) {
    const {ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {name, categories, instructor} = listing;

    // Figma draws the three tags in this order. Each is omitted entirely when its value is
    // absent, and with all three absent the row does not render — which is a state real
    // data reaches: 3 of the 9 listings on dev have no stats at all.
    const stats: ReactNode[] = [];

    if (instructor.courseCount !== undefined) {
        stats.push(
            <Tag
                key="courses"
                label={ninterpolate(
                    '%(count)s Course',
                    '%(count)s Courses',
                    instructor.courseCount,
                    {
                        // Localised digits (`1,289`, not `1289`). This deviates from Figma,
                        // which draws raw digits — see the data plan's open item 10.
                        count: formatNumber(instructor.courseCount),
                    },
                )}
            />,
        );
    }

    if (instructor.averageRating !== undefined) {
        // `RatingTag` renders the star and rounds to one decimal through `useFormatNumber`,
        // which is doing real work here: ratings arrive at full float precision
        // (`4.66542911529541`). It also carries the "Rating: x out of 5" screen-reader text.
        stats.push(<RatingTag key="rating" rating={instructor.averageRating} />);
    }

    if (instructor.learnerCount !== undefined) {
        stats.push(
            <Tag
                key="learners"
                label={ninterpolate(
                    '%(count)s student',
                    '%(count)s students',
                    instructor.learnerCount,
                    {count: formatNumber(instructor.learnerCount)},
                )}
            />,
        );
    }

    return (
        <li
            className={classNames(styles['item'], {[styles['hidden-on-mobile']]: isHiddenOnMobile})}
        >
            <TrackImpression trackFunc={onPresented ?? noop} testId={CARD_IMPRESSION_TEST_ID}>
                <a
                    className={styles['card']}
                    href={instructor.profileUrl}
                    onClick={onSelected}
                    data-purpose="community-card"
                >
                    <span className={styles['avatar']}>
                        {instructor.avatarUrl ? (
                            <Image
                                className={styles['avatar-image']}
                                src={instructor.avatarUrl}
                                alt=""
                                // The intrinsic size varies by asset, and CSS fixes the rendered
                                // box, so there is no reflow to guard against.
                                width="unset"
                                height="unset"
                            />
                        ) : (
                            // `images.height200` and `px50x50` are independently nullable, so a
                            // listing can arrive with no photo. Figma draws no such state; this
                            // matches what the design system's own `Avatar` falls back to.
                            <span className={styles['avatar-fallback']}>
                                <Icon glyph="person" size="xxlarge" label={false} color="inherit" />
                            </span>
                        )}
                    </span>
                    <span className={styles['text']}>
                        <span className={styles['identity']}>
                            <span className={styles['instructor-name']}>{instructor.name}</span>
                            {stats.length > 0 && <span className={styles['stats']}>{stats}</span>}
                        </span>
                        <span className={styles['community']}>
                            <span className={styles['community-name']}>{name}</span>
                            {/* Display names, joined as Figma draws them. Most listings on dev
                            come back with none, hence the guard. */}
                            {categories.length > 0 && (
                                <span className={styles['categories']}>
                                    {categories.join(', ')}
                                </span>
                            )}
                        </span>
                    </span>
                </a>
            </TrackImpression>
        </li>
    );
}
